import { useMemo, useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useLoaderData } from "react-router-dom";
import colors from "../../styles/colors";
import CommonWrapper from "../../components/ui_components/CommonComponentsStructureCss";
import { apiListBeltsV2, apiCreateBeltV2, apiUpdateBeltV2 } from "../../api/beltApi";
import CreateBeltModale from "../../components/admin/BeltHandleFolder/CreateBeltModale";
import type { Belt } from "../../types";

const Container = styled.div`
    width: 80%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-top: 20px;

    & h1.admin-belt-handle-title {
        font-size: 24px;
        font-weight: 600;
        text-align: center;
        margin-bottom: 10px;
    }

    & p.admin-belt-handle-description {
        font-size: 16px;
        font-weight: 400;
        text-align: center;
        margin-bottom: 10px;
    }
`;

const DivForButtonAggiungiBelt = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: 20px 0;

    & button.btn-handle-aggiungi-belt {
        width: 300px;
        padding: 10px;
        border-radius: 5px;
        border: 1px solid white;
        background-color: ${colors.bgColorBlack2};
        color: white;
        font-size: 20px;
        text-transform: uppercase;
        cursor: pointer;
        transition: transform 0.2s ease-in-out, background-color 0.2s ease-in-out;

        &:hover {
            transform: scale(1.05);
            background-color: ${colors.bgColorBlack};
        }
    }
`;

const BeltsContainer = styled.div`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-bottom: 40px;
`;

const BeltBox = styled.div`
    width: calc(33.333% - 10px);
    min-width: 280px;
    background-color: ${colors.bgColorBlack2};
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    gap: 12px;
    cursor: pointer;
    transition: border-color 0.2s ease-in-out, transform 0.2s ease-in-out;

    &:hover {
        border-color: gold;
        transform: scale(1.02);
    }

    @media (max-width: 900px) {
        width: calc(50% - 7px);
    }

    @media (max-width: 600px) {
        width: 100%;
    }
`;

const BeltImage = styled.img`
    width: 120px;
    height: 70px;
    object-fit: contain;
    border-radius: 4px;
    background-color: rgba(255, 255, 255, 0.05);
`;

const BeltImagePlaceholder = styled.div`
    width: 120px;
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    background-color: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
    font-size: 12px;
`;

const BeltInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    text-align: center;
`;

const BeltName = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: gold;
`;

const BeltMeta = styled.div`
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
`;

const InfoBox = styled.div`
    width: 100%;
    background-color: ${colors.bgColorBlack2};
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 14px;
    margin-top: 10px;
    color: rgba(255, 255, 255, 0.85);
    text-align: center;
`;

function BeltHandle() {
    const loaderData = useLoaderData() as Belt[] | { data: Belt[] } | null;

    const initialBelts = useMemo(() => {
        if (!loaderData) return [];
        if (Array.isArray(loaderData)) return loaderData;
        if (loaderData && typeof loaderData === "object" && "data" in loaderData) {
            return Array.isArray(loaderData.data) ? loaderData.data : [];
        }
        return [];
    }, [loaderData]);

    const [belts, setBelts] = useState<Belt[]>(initialBelts);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [editBelt, setEditBelt] = useState<Belt | null>(null);
    const fetchAbortRef = useRef<AbortController | null>(null);
    useEffect(() => { return () => { fetchAbortRef.current?.abort(); }; }, []);

    const sortedBelts = useMemo(() => {
        return [...belts].sort((a, b) => (a.name || "").localeCompare(b.name || "", "it"));
    }, [belts]);

    const fetchAllBelts = async () => {
        if (fetchAbortRef.current) fetchAbortRef.current.abort();
        fetchAbortRef.current = new AbortController();
        const { signal } = fetchAbortRef.current;
        setIsLoading(true);
        try {
            const response = await apiListBeltsV2() as any;
            if (signal.aborted) return;
            if (response?.error) {
                console.error("Errore nel caricare le cinture:", response);
                return;
            }
            const data = response?.data ?? response;
            setBelts(Array.isArray(data) ? data : []);
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') return;
            console.error("Errore nel caricare le cinture:", error);
        } finally {
            if (!signal.aborted) setIsLoading(false);
        }
    };

    const handleCreateBelt = async (formData: FormData) => {
        const response = await apiCreateBeltV2(formData) as any;

        if (response?.errors) {
            const firstErrorKey = Object.keys(response.errors)[0];
            const firstErrorValue = response.errors[firstErrorKey];
            const message = Array.isArray(firstErrorValue) ? firstErrorValue[0] : (firstErrorValue || "Errore di validazione");
            throw new Error(message);
        }
        if (response?.error) {
            throw new Error(response.error);
        }

        await fetchAllBelts();
        return response;
    };

    const handleUpdateBelt = async (id: number, formData: FormData) => {
        const response = await apiUpdateBeltV2(id, formData) as any;

        if (response?.errors) {
            const firstErrorKey = Object.keys(response.errors)[0];
            const firstErrorValue = response.errors[firstErrorKey];
            const message = Array.isArray(firstErrorValue) ? firstErrorValue[0] : (firstErrorValue || "Errore di validazione");
            throw new Error(message);
        }
        if (response?.error) {
            throw new Error(response.error);
        }

        await fetchAllBelts();
        return response;
    };

    const handleSave = async (formData: FormData, beltId?: number) => {
        if (beltId) {
            return handleUpdateBelt(beltId, formData);
        }
        return handleCreateBelt(formData);
    };

    const handleBeltClick = (belt: Belt) => {
        setEditBelt(belt);
        setIsModalOpen(true);
    };

    const handleOpenCreate = () => {
        setEditBelt(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditBelt(null);
    };

    return (
        <CommonWrapper className="admin-belt-handle-common-wrapper">
            <Container className="admin-belt-handle-container">
                <h1 className="admin-belt-handle-title">Gestisci Cinture</h1>
                <p className="admin-belt-handle-description">
                    Qui puoi visualizzare le cinture e aggiungerne di nuove o modificare quelle esistenti.
                </p>

                <DivForButtonAggiungiBelt className="admin-belt-handle-button-aggiungi-belt">
                    <button
                        className="btn-handle-aggiungi-belt"
                        onClick={handleOpenCreate}
                        disabled={isLoading}
                    >
                        {isLoading ? "Caricamento..." : "Aggiungi Cintura"}
                    </button>
                </DivForButtonAggiungiBelt>

                {sortedBelts.length === 0 && (
                    <InfoBox className="admin-belt-handle-empty">
                        Nessuna cintura disponibile.{" "}
                        <button
                            type="button"
                            className="btn btn-link"
                            style={{ color: "gold", textDecoration: "underline" }}
                            onClick={fetchAllBelts}
                        >
                            Ricarica
                        </button>
                    </InfoBox>
                )}

                {sortedBelts.length > 0 && (
                    <BeltsContainer className="admin-belt-handle-belts-container">
                        {sortedBelts.map((belt) => (
                            <BeltBox
                                key={belt.id}
                                className="admin-belt-handle-belt-box"
                                onClick={() => handleBeltClick(belt)}
                            >
                                {belt.img_path ? (
                                    <BeltImage
                                        src={process.env.REACT_APP_STORAGE_URL + belt.img_path}
                                        alt={belt.name}
                                        className="admin-belt-handle-belt-image"
                                    />
                                ) : (
                                    <BeltImagePlaceholder className="admin-belt-handle-belt-image-placeholder">
                                        No img
                                    </BeltImagePlaceholder>
                                )}
                                <BeltInfo className="admin-belt-handle-belt-info">
                                    <BeltName className="admin-belt-handle-belt-name">
                                        {belt.name}
                                    </BeltName>
                                    <BeltMeta className="admin-belt-handle-belt-meta">
                                        <span>{belt.is_team ? "Tag Team" : "Singles"}</span>
                                        <span>•</span>
                                        <span>{belt.is_active ? "Attiva" : "Inattiva"}</span>
                                        {belt.importance && (
                                            <>
                                                <span>•</span>
                                                <span>Imp: {belt.importance}</span>
                                            </>
                                        )}
                                    </BeltMeta>
                                </BeltInfo>
                            </BeltBox>
                        ))}
                    </BeltsContainer>
                )}

                {isModalOpen && (
                    <CreateBeltModale
                        closeModalClick={handleCloseModal}
                        onSave={handleSave}
                        editBelt={editBelt}
                    />
                )}
            </Container>
        </CommonWrapper>
    );
}

export default BeltHandle;
